import { useState, useEffect } from "react"
import { api } from "../utils/apiComponent/apiClient"
import { endpoints } from "../utils/endpoints"

export function useAdminStats(selectedYear = null, selectedMonth = null) {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalEnrolled: 0,
    totalRevenue: 0,
    totalCourses: 0
  })
  const [recentUsers, setRecentUsers] = useState([])
  const [enrollmentData, setEnrollmentData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    const fetchAdminStats = async () => {
      setLoading(true)
      setError(null)

      try {
        // Récupérer toutes les données en parallèle
        const [usersResponse, enrollmentsResponse, coursesResponse, paymentsResponse] = await Promise.allSettled([
          // 1. Total des utilisateurs
          api.get(endpoints.users),
          // 2. Total des inscrits (enrollments avec status actif)
          api.get(endpoints.enrollments),
          // 3. Total des cours
          api.get(endpoints.courses),
          // 4. Total des revenus (statistiques de paiement)
          api.get(endpoints.adminPaymentStats).catch(() => null)
        ])

        // Traiter les réponses
        let totalUsers = 0
        let totalEnrolled = 0
        let totalCourses = 0
        let totalRevenue = 0

        // Total des utilisateurs
        if (usersResponse.status === 'fulfilled' && usersResponse.value) {
          console.log('[useAdminStats] Raw usersResponse.value:', usersResponse.value)
          console.log('[useAdminStats] Is array?', Array.isArray(usersResponse.value))
          
          // Gérer différentes structures de réponse
          let users = []
          if (Array.isArray(usersResponse.value)) {
            users = usersResponse.value
          } else if (usersResponse.value.data && Array.isArray(usersResponse.value.data)) {
            users = usersResponse.value.data
          } else if (usersResponse.value.users && Array.isArray(usersResponse.value.users)) {
            users = usersResponse.value.users
          } else if (usersResponse.value.result && Array.isArray(usersResponse.value.result)) {
            users = usersResponse.value.result
          }
          
          // Utiliser le champ 'total' de la pagination si disponible, sinon compter le tableau
          if (usersResponse.value.total !== undefined) {
            totalUsers = Number(usersResponse.value.total)
            console.log('[useAdminStats] Total des utilisateurs depuis le champ total:', totalUsers)
          } else if (usersResponse.value.data?.total !== undefined) {
            totalUsers = Number(usersResponse.value.data.total)
            console.log('[useAdminStats] Total des utilisateurs depuis data.total:', totalUsers)
          } else {
            totalUsers = users.length
            console.log('[useAdminStats] Total des utilisateurs depuis la longueur du tableau:', totalUsers)
          }
          
          console.log('[useAdminStats] Users extraits:', users.length, 'utilisateurs sur cette page')
          console.log('[useAdminStats] Exemple user:', users[0])
          
          // Prendre les 5 derniers utilisateurs
          const sortedUsers = users.sort((a, b) => 
            new Date(b.createdAt || b.created_at || 0) - new Date(a.createdAt || a.created_at || 0)
          )
          const recentUsersList = sortedUsers.slice(0, 5)
          console.log('[useAdminStats] 5 derniers utilisateurs:', recentUsersList.map(u => ({ id: u.id, name: u.name, email: u.email, city: u.city, country: u.country })))
          setRecentUsers(recentUsersList)
        } else {
          console.warn('[useAdminStats] usersResponse.status:', usersResponse.status)
          if (usersResponse.status === 'rejected') {
            console.error('[useAdminStats] usersResponse.reason:', usersResponse.reason)
            console.error('[useAdminStats] usersResponse.reason details:', usersResponse.reason?.message, usersResponse.reason?.stack)
          } else if (!usersResponse.value) {
            console.warn('[useAdminStats] usersResponse.value est null ou undefined')
          }
        }

        // Total des inscrits (enrollments actifs) et calcul par mois
        if (enrollmentsResponse.status === 'fulfilled' && enrollmentsResponse.value) {
          console.log('[useAdminStats] Raw enrollmentsResponse.value:', enrollmentsResponse.value)
          console.log('[useAdminStats] enrollmentsResponse.value type:', typeof enrollmentsResponse.value)
          console.log('[useAdminStats] Is array?', Array.isArray(enrollmentsResponse.value))
          
          // Gérer différentes structures de réponse
          let enrollments = []
          if (Array.isArray(enrollmentsResponse.value)) {
            enrollments = enrollmentsResponse.value
            console.log('[useAdminStats] Enrollments est un tableau direct')
          } else if (enrollmentsResponse.value.data && Array.isArray(enrollmentsResponse.value.data)) {
            enrollments = enrollmentsResponse.value.data
            console.log('[useAdminStats] Enrollments dans .data')
          } else if (enrollmentsResponse.value.enrollments && Array.isArray(enrollmentsResponse.value.enrollments)) {
            enrollments = enrollmentsResponse.value.enrollments
            console.log('[useAdminStats] Enrollments dans .enrollments')
          } else if (enrollmentsResponse.value.result && Array.isArray(enrollmentsResponse.value.result)) {
            enrollments = enrollmentsResponse.value.result
            console.log('[useAdminStats] Enrollments dans .result')
          } else {
            console.warn('[useAdminStats] Structure de réponse inattendue pour enrollments:', Object.keys(enrollmentsResponse.value || {}))
          }
          
          console.log('[useAdminStats] Enrollments extraits:', enrollments.length)
          if (enrollments.length > 0) {
            console.log('[useAdminStats] Exemple enrollment:', enrollments[0])
            console.log('[useAdminStats] Structure complète de l\'enrollment:', JSON.stringify(enrollments[0], null, 2))
            console.log('[useAdminStats] Tous les status d\'enrollments:', enrollments.map(e => ({ id: e.id, status: e.status, courseId: e.courseId })))
            console.log('[useAdminStats] Enrollments avec informations de paiement:', enrollments.filter(e => e.paymentAmount || e.price || e.amount || e.payment || e.paymentId).map(e => ({ 
              id: e.id, 
              paymentAmount: e.paymentAmount, 
              price: e.price, 
              amount: e.amount,
              payment: e.payment,
              paymentId: e.paymentId
            })))
          }
          
          // Compter uniquement les inscriptions actives
          // Essayer différentes variantes de status
          const activeEnrollments = enrollments.filter(e => {
            const status = (e.status || '').toString().toLowerCase()
            const isActive = status === 'active' || status === 'actif' || status === 'enrolled' || status === 'inscrit'
            // Si pas de status, considérer comme actif (par défaut)
            return isActive || !e.status
          })
          
          totalEnrolled = activeEnrollments.length
          
          console.log('[useAdminStats] Total enrollments:', enrollments.length)
          console.log('[useAdminStats] Active enrollments:', activeEnrollments.length)
          console.log('[useAdminStats] Total enrolled (actifs):', totalEnrolled)

          // Calculer les inscriptions par mois
          const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']
          const last6Months = []
          
          // Mois de début (le mois sélectionné) ou mois actuel par défaut
          const startYear = selectedYear !== null ? selectedYear : new Date().getFullYear()
          const startMonth = selectedMonth !== null ? selectedMonth : new Date().getMonth()
          
          // Calculer les 6 mois à partir du mois sélectionné
          for (let i = 0; i < 6; i++) {
            const targetDate = new Date(startYear, startMonth + i)
            
            const targetYear = targetDate.getFullYear()
            const targetMonth = targetDate.getMonth()
            const monthName = monthNames[targetMonth]
            const monthKey = `${monthName} ${targetYear}`
            
            last6Months.push({
              month: monthName,
              year: targetYear,
              monthKey: monthKey,
              enrolled: 0
            })
          }
          
          // Compter les inscriptions actives pour chaque mois
          activeEnrollments.forEach(enrollment => {
            const date = new Date(enrollment.createdAt || enrollment.created_at || enrollment.enrolledAt || enrollment.enrolled_at || enrollment.date || Date.now())
            const enrollmentYear = date.getFullYear()
            const enrollmentMonth = date.getMonth()
            
            console.log('[useAdminStats] Processing enrollment:', {
              id: enrollment.id,
              date: date.toISOString(),
              year: enrollmentYear,
              month: enrollmentMonth,
              monthName: monthNames[enrollmentMonth]
            })
            
            // Trouver le mois correspondant dans last6Months
            last6Months.forEach(monthData => {
              if (monthData.year === enrollmentYear && monthNames[enrollmentMonth] === monthData.month) {
                monthData.enrolled += 1
                console.log('[useAdminStats] Enrollment ajouté au mois:', monthData.month, monthData.year, 'total:', monthData.enrolled)
              }
            })
          })
          
          console.log('[useAdminStats] Enrollment data par mois:', last6Months)
          
          setEnrollmentData(last6Months)
        } else {
          console.warn('[useAdminStats] enrollmentsResponse.status:', enrollmentsResponse.status)
          if (enrollmentsResponse.status === 'rejected') {
            console.error('[useAdminStats] enrollmentsResponse.reason:', enrollmentsResponse.reason)
            console.error('[useAdminStats] enrollmentsResponse.reason details:', enrollmentsResponse.reason?.message, enrollmentsResponse.reason?.stack)
          } else if (!enrollmentsResponse.value) {
            console.warn('[useAdminStats] enrollmentsResponse.value est null ou undefined')
          }
        }

        // Total des cours
        if (coursesResponse.status === 'fulfilled' && coursesResponse.value) {
          console.log('[useAdminStats] Raw coursesResponse.value:', coursesResponse.value)
          
          // Gérer différentes structures de réponse
          let courses = []
          if (Array.isArray(coursesResponse.value)) {
            courses = coursesResponse.value
          } else if (coursesResponse.value.data && Array.isArray(coursesResponse.value.data)) {
            courses = coursesResponse.value.data
          } else if (coursesResponse.value.courses && Array.isArray(coursesResponse.value.courses)) {
            courses = coursesResponse.value.courses
          } else if (coursesResponse.value.result && Array.isArray(coursesResponse.value.result)) {
            courses = coursesResponse.value.result
          }
          
          totalCourses = courses.length
          console.log('[useAdminStats] Courses extraits:', totalCourses)
        } else {
          console.log('[useAdminStats] coursesResponse.status:', coursesResponse.status)
          if (coursesResponse.status === 'rejected') {
            console.log('[useAdminStats] coursesResponse.reason:', coursesResponse.reason)
          }
        }

        // Total des revenus
        if (paymentsResponse.status === 'fulfilled' && paymentsResponse.value) {
          console.log('[useAdminStats] Raw paymentsResponse.value:', paymentsResponse.value)
          console.log('[useAdminStats] paymentsResponse.value type:', typeof paymentsResponse.value)
          console.log('[useAdminStats] Is array?', Array.isArray(paymentsResponse.value))
          
          // Si l'endpoint retourne une structure avec totalRevenue ou totalAmount
          if (paymentsResponse.value.totalRevenue !== undefined) {
            totalRevenue = Number(paymentsResponse.value.totalRevenue) || 0
            console.log('[useAdminStats] Total revenue trouvé dans .totalRevenue:', totalRevenue)
          } else if (paymentsResponse.value.totalAmount !== undefined) {
            // L'endpoint retourne totalAmount pour les paiements réussis
            totalRevenue = Number(paymentsResponse.value.totalAmount) || 0
            console.log('[useAdminStats] Total revenue trouvé dans .totalAmount:', totalRevenue)
          } else if (paymentsResponse.value.revenue !== undefined) {
            totalRevenue = Number(paymentsResponse.value.revenue) || 0
            console.log('[useAdminStats] Total revenue trouvé dans .revenue:', totalRevenue)
          } else if (paymentsResponse.value.data?.totalRevenue !== undefined) {
            totalRevenue = Number(paymentsResponse.value.data.totalRevenue) || 0
            console.log('[useAdminStats] Total revenue trouvé dans .data.totalRevenue:', totalRevenue)
          } else if (paymentsResponse.value.data?.totalAmount !== undefined) {
            totalRevenue = Number(paymentsResponse.value.data.totalAmount) || 0
            console.log('[useAdminStats] Total revenue trouvé dans .data.totalAmount:', totalRevenue)
          } else if (paymentsResponse.value.data?.revenue !== undefined) {
            totalRevenue = Number(paymentsResponse.value.data.revenue) || 0
            console.log('[useAdminStats] Total revenue trouvé dans .data.revenue:', totalRevenue)
          } else if (paymentsResponse.value.paymentsByStatus && Array.isArray(paymentsResponse.value.paymentsByStatus)) {
            // Calculer à partir de paymentsByStatus pour les paiements SUCCESS
            const successPayment = paymentsResponse.value.paymentsByStatus.find(p => 
              p.status === 'SUCCESS' || p.status === 'success' || p.status?.toLowerCase() === 'success'
            )
            if (successPayment && successPayment._sum && successPayment._sum.amount) {
              totalRevenue = Number(successPayment._sum.amount) || 0
              console.log('[useAdminStats] Total revenue calculé à partir de paymentsByStatus:', totalRevenue)
            }
          } else {
            // Calculer manuellement si on a des paiements
            let payments = []
            if (Array.isArray(paymentsResponse.value)) {
              payments = paymentsResponse.value
              console.log('[useAdminStats] Payments est un tableau direct')
            } else if (paymentsResponse.value.data && Array.isArray(paymentsResponse.value.data)) {
              payments = paymentsResponse.value.data
              console.log('[useAdminStats] Payments dans .data')
            } else if (paymentsResponse.value.payments && Array.isArray(paymentsResponse.value.payments)) {
              payments = paymentsResponse.value.payments
              console.log('[useAdminStats] Payments dans .payments')
            } else if (paymentsResponse.value.result && Array.isArray(paymentsResponse.value.result)) {
              payments = paymentsResponse.value.result
              console.log('[useAdminStats] Payments dans .result')
            } else {
              console.warn('[useAdminStats] Structure de réponse inattendue pour payments:', Object.keys(paymentsResponse.value || {}))
              console.log('[useAdminStats] Structure complète de paymentsResponse.value:', JSON.stringify(paymentsResponse.value, null, 2))
              
              // Essayer de trouver les paiements dans d'autres propriétés possibles
              const possibleKeys = ['payments', 'data', 'result', 'items', 'list', 'content', 'records']
              for (const key of possibleKeys) {
                if (paymentsResponse.value[key] && Array.isArray(paymentsResponse.value[key])) {
                  payments = paymentsResponse.value[key]
                  console.log(`[useAdminStats] Payments trouvés dans .${key}:`, payments.length)
                  break
                }
              }
            }
            
            console.log('[useAdminStats] Payments extraits:', payments.length)
            if (payments.length > 0) {
              console.log('[useAdminStats] Exemple payment:', payments[0])
              console.log('[useAdminStats] Tous les status de payments:', payments.map(p => ({ id: p.id, status: p.status, amount: p.amount || p.price })))
            }
            
            // Somme des paiements réussis
            const successfulPayments = payments.filter(p => {
              const status = (p.status || '').toString().toLowerCase()
              return status === 'paid' || status === 'success' || status === 'completed' || status === 'SUCCESS'
            })
            
            console.log('[useAdminStats] Successful payments:', successfulPayments.length)
            console.log('[useAdminStats] Successful payments details:', successfulPayments.map(p => ({ id: p.id, status: p.status, amount: p.amount || p.price })))
            
            totalRevenue = successfulPayments.reduce((sum, p) => {
              const amount = p.amount || p.price || 0
              console.log('[useAdminStats] Adding payment amount:', amount, 'total so far:', sum + amount)
              return sum + amount
            }, 0)
          }
          
          console.log('[useAdminStats] Total revenue calculé:', totalRevenue)
        } else {
          console.warn('[useAdminStats] paymentsResponse.status:', paymentsResponse.status)
          if (paymentsResponse.status === 'rejected') {
            console.error('[useAdminStats] paymentsResponse.reason:', paymentsResponse.reason)
            console.error('[useAdminStats] paymentsResponse.reason details:', paymentsResponse.reason?.message, paymentsResponse.reason?.stack)
          } else if (!paymentsResponse.value) {
            console.warn('[useAdminStats] paymentsResponse.value est null ou undefined')
          }
          
          // Si l'endpoint des paiements échoue, essayer de calculer les revenus à partir des inscriptions
          // si elles contiennent des informations de paiement
          if (enrollmentsResponse.status === 'fulfilled' && enrollmentsResponse.value) {
            console.log('[useAdminStats] Tentative de calculer les revenus à partir des inscriptions...')
            const enrollments = Array.isArray(enrollmentsResponse.value) 
              ? enrollmentsResponse.value 
              : enrollmentsResponse.value?.data || enrollmentsResponse.value?.enrollments || []
            
            // Chercher les inscriptions avec des informations de paiement
            const enrollmentsWithPayment = enrollments.filter(e => e.paymentAmount || e.price || e.amount)
            console.log('[useAdminStats] Enrollments avec paiement:', enrollmentsWithPayment.length)
            
            if (enrollmentsWithPayment.length > 0) {
              totalRevenue = enrollmentsWithPayment.reduce((sum, e) => {
                const amount = e.paymentAmount || e.price || e.amount || 0
                return sum + amount
              }, 0)
              console.log('[useAdminStats] Total revenue calculé à partir des inscriptions:', totalRevenue)
            }
          }
        }

        console.log('[useAdminStats] Stats finales:', { totalUsers, totalEnrolled, totalCourses, totalRevenue })
        
        setStats({
          totalUsers,
          totalEnrolled,
          totalCourses,
          totalRevenue
        })
      } catch (err) {
        console.error("Erreur lors de la récupération des statistiques admin:", err)
        setError(err.message || "Erreur lors du chargement des statistiques")
        
        // En cas d'erreur, garder les valeurs par défaut
        setStats({
          totalUsers: 0,
          totalEnrolled: 0,
          totalCourses: 0,
          totalRevenue: 0
        })
      } finally {
        setLoading(false)
      }
    }
    
    fetchAdminStats()
  }, [selectedYear, selectedMonth, refreshKey])

  // Fonction pour recharger les données
  const refetch = () => {
    console.log('[useAdminStats] refetch() appelé, refreshKey va passer à:', refreshKey + 1)
    setRefreshKey(prev => prev + 1)
  }

  return {
    stats,
    recentUsers,
    enrollmentData,
    loading,
    error,
    refetch
  }
}
